import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";

/**
 * Login — split-screen premium layout.
 * Left: heritage photo + Playfair tagline + brand facts.
 * Right: minimal, confident form.
 */
export default function LoginPage() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await login(email, password);
    setSubmitting(false);
    if (ok) navigate("/anuncios");
  };

  return (
    <div className="min-h-screen flex bg-ronil-light">
      {/* ------------- Hero rail ------------- */}
      <aside
        className="hidden md:flex md:w-1/2 lg:w-[55%] text-white p-12 lg:p-16 flex-col justify-between relative overflow-hidden bg-ronil-navy"
        style={{
          backgroundImage: "url('/heritage/ronil-salao-vendas.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(12,26,51,0.95) 0%, rgba(31,56,100,0.80) 50%, rgba(31,56,100,0.55) 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 16px, white 16px, white 17px)" }}
        />

        {/* Brand mark */}
        <div className="relative">
          <div className="flex items-center gap-3 mb-16">
            <div className="w-11 h-11 bg-white text-ronil-navy font-display font-bold text-xl rounded-sm flex items-center justify-center shadow-elevation">R</div>
            <div>
              <div className="font-heading font-black text-xl tracking-[0.08em]">RONIL</div>
              <div className="text-[10px] text-ronil-sand uppercase tracking-[0.3em] mt-0.5">LDA · Moçambique · Desde 1946</div>
            </div>
          </div>

          <h1 className="hero-tagline text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08] mb-5 max-w-xl">
            Liderança em movimento,
            <br />
            <span className="italic text-ronil-sand">há 80 anos.</span>
          </h1>
          <p className="font-body text-slate-200/90 text-sm lg:text-base leading-relaxed max-w-md">
            Command Center — a torre de controlo das operações da Ronil LDA. Oficinas, peças, BPS, Recepções, CRM, garantias, planos de manutenção e reportes num só painel.
          </p>

          <div className="mt-14 grid grid-cols-2 gap-6 text-sm max-w-lg">
            <div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-ronil-sand/80">Marcas</div>
              <div className="font-heading font-semibold mt-2 leading-6">
                BMW · Mazda<br />Hyundai · GWM
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.3em] text-ronil-sand/80">Filiais</div>
              <div className="font-heading font-semibold mt-2 leading-[1.55]">
                Maputo · Inhambane · Vilanculos<br />Chimoio · Beira · Tete<br />Quelimane · Nampula · Nacala<br />Lichinga · Pemba · Afungi
              </div>
            </div>
          </div>
        </div>

        {/* Heritage footer */}
        <div className="relative">
          <div className="border-l-2 border-ronil-sand/70 pl-4 mb-4" data-testid="login-heritage-caption">
            <div className="text-[9px] uppercase tracking-[0.3em] text-ronil-sand/80">Herança</div>
            <div className="text-xs text-white/90 font-display italic mt-1">
              Salão de Vendas · Ronil, Lourenço Marques
            </div>
          </div>
          <div className="text-[10px] text-slate-400 font-mono tracking-wider">
            RONIL COMMAND CENTER · © {new Date().getFullYear()} RONIL LDA · V1.0
          </div>
        </div>
      </aside>

      {/* ------------- Form rail ------------- */}
      <section className="flex-1 flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          {/* Mobile-only brand mark */}
          <div className="md:hidden flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-ronil-navy text-white font-display font-bold text-lg rounded-sm flex items-center justify-center">R</div>
            <div>
              <div className="font-heading font-black text-base tracking-[0.08em] text-ronil-navy">RONIL</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-[0.3em]">Desde 1946</div>
            </div>
          </div>

          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-ronil-navy font-heading font-bold mb-3">Área reservada</div>
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2 tracking-tight">Bem-vindo</h2>
            <p className="text-sm text-slate-500">Aceda com as suas credenciais Ronil.</p>
          </div>

          <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-2">
            Endereço de email
          </label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            placeholder="utilizador@ronil-auto.co.mz"
            className="w-full mb-5 px-3.5 py-2.5 border border-slate-300 rounded-md bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition"
          />

          <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-2">
            Palavra-passe
          </label>
          <div className="relative mb-2">
            <input
              data-testid="login-password-input"
              type={showPw ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full px-3.5 py-2.5 pr-10 border border-slate-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition"
            />
            <button
              type="button"
              data-testid="login-toggle-password"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Esconder palavra-passe" : "Mostrar palavra-passe"}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ronil-navy p-1 transition-colors"
            >
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <div className="mb-6 text-right">
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-xs text-ronil-navy hover:text-ronil-navy-hover hover:underline font-medium"
            >
              Esqueceu-se da palavra-passe?
            </Link>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="mb-5 px-4 py-2.5 bg-red-50 text-red-800 border border-red-200 rounded-md text-sm flex items-start gap-2"
            >
              <span>{error}</span>
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md py-3 text-sm font-heading font-semibold tracking-wide transition-all disabled:opacity-60 flex items-center justify-center gap-2 shadow-sm hover:shadow-elevation group"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Iniciar sessão
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </>
            )}
          </button>

          <div className="mt-10 pt-6 border-t border-slate-200 text-[11px] text-slate-400 text-center">
            Sessão protegida · Ronil LDA · Moçambique
          </div>
        </form>
      </section>
    </div>
  );
}
